/**
 * Scene graph: arena floor, sonar grid and the background parallax field.
 *
 * §14.1 B8 — exactly two real dynamic lights exist, the player aura and the boss, and
 * they light one surface: the trench floor. Everything else in the scene is emissive
 * plus bloom. Shadow maps are off and no other light type is ever constructed.
 *
 * §14.2 budget for this file: floor = 1 draw, parallax = 3 draws. AMD-24 deleted the
 * wall-rim batch outright — a torus has no wall to rim — which is one draw call back.
 */

import * as THREE from 'three';
import { WRAP_H, WRAP_W, wrapDeltaX, wrapDeltaY } from '../sim/wrap';
import { GasField } from './gas';
import { InstancedBatch, LAYER_FLOOR, LAYER_PARALLAX, MODE_SPRITE } from './instanced';
import { LINEAR, tokenIndex } from './palette';
import { quality } from './quality';
import { buildQuadGeometry } from './silhouettes';
import { FLOOR_FRAG, FLOOR_VERT } from './shaders/instanced';
import { unwrappedWarpUniforms } from './warp';

const TOK_BG = tokenIndex('pal.ui.bg');
const TOK_GRID = tokenIndex('pal.ui.grid');
const TOK_GRID_MAJOR = tokenIndex('pal.ui.gridmajor');
const TOK_DECOR = tokenIndex('pal.ui.decor');
const TOK_PLAYER_AURA = tokenIndex('pal.player.aura');

/**
 * AMD-24 — parallax on a torus.
 *
 * The old field was a lattice tiled around the camera at a period derived from the view
 * size, with each layer's phase offset by `camera × factor`. On a bounded arena that is
 * fine. On a torus it is not: when the camera crosses the seam its coordinate jumps by
 * 200 u, the phase jumps by a non-integral number of tiles, and the whole background
 * snaps sideways. The seam would be invisible in every layer except the one the eye uses
 * to judge motion.
 *
 * Each layer is now a fixed point set ON the 200 × 200 torus — real world positions,
 * hashed once at boot — drawn at a depth-compressed offset from the camera:
 *
 *     screen offset = wrapDelta(mote − camera) × depth
 *
 * Both terms are seam-safe. `wrapDelta` is continuous everywhere except at the antipode,
 * `depth` is a constant, and a lap of the arena returns every mote to the same pixel
 * because the point set is genuinely periodic at 200. Depth is also the parallax rate:
 * at 0.55 the layer slides at 55% of the world's screen speed, so the three layers still
 * separate exactly as before.
 *
 * `depth × 100` is where the antipode lands, in units from the frame centre. The widest
 * framing is 87 u (68 u × the 1.28 cinematic zoom), so its half-width is 43.5 u and the
 * smallest depth here keeps the discontinuity 11.5 u outside the frame. The per-mote cull
 * below makes that a belt as well as braces: a mote that is off screen is not pushed, so
 * the wrap happens to instances that are not being drawn.
 */
const PARALLAX_LAYERS: readonly { count: number; depth: number; size: number; alpha: number }[] = [
  { count: 72, depth: 0.55, size: 0.09, alpha: 0.22 },
  { count: 72, depth: 0.72, size: 0.14, alpha: 0.3 },
  { count: 64, depth: 0.88, size: 0.2, alpha: 0.38 },
];

/**
 * Floor lattice subdivision. The curvature is a per-vertex transform, so the mesh has to
 * have vertices to bend: 32 × 32 cells is 2,048 triangles in one draw call, and at the
 * widest framing that is a control point every 2.7 u of screen, well below the scale at
 * which the eye reads a straight line as straight.
 */
const FLOOR_CELLS = 32;

/**
 * AMD-6 D13 explosion ripples: (x, y, startTime, unused). Preallocated at module scope
 * and mutated in place — the floor material holds the same four vectors for its lifetime,
 * so recording a ripple allocates nothing.
 */
const RIPPLES: THREE.Vector4[] = [
  new THREE.Vector4(0, 0, -100, 0),
  new THREE.Vector4(0, 0, -100, 0),
  new THREE.Vector4(0, 0, -100, 0),
  new THREE.Vector4(0, 0, -100, 0),
];

/**
 * A subdivided [-1, 1]² quad. `FLOOR_VERT` maps it onto the world through the lattice
 * centre and half-extent, so the same buffer serves every framing.
 */
function buildFloorLattice(cells: number): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const verts = new Float32Array(cells * cells * 6 * 3);
  let n = 0;
  const step = 2 / cells;
  for (let j = 0; j < cells; j++) {
    const y0 = -1 + j * step;
    const y1 = y0 + step;
    for (let i = 0; i < cells; i++) {
      const x0 = -1 + i * step;
      const x1 = x0 + step;
      const quad = [x0, y0, x1, y0, x1, y1, x0, y0, x1, y1, x0, y1];
      for (let k = 0; k < 12; k += 2) {
        verts[n++] = quad[k];
        verts[n++] = quad[k + 1];
        verts[n++] = 0;
      }
    }
  }
  geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
  return geo;
}

/**
 * Sets a light's colour from a palette token, keeping §11.14 criterion 1 (no raw hex
 * outside `palette.ts`) true for the only two real lights in the scene (§14.1 B8).
 */
function setLightFromToken(light: THREE.PointLight, token: string): void {
  const i = tokenIndex(token) * 3;
  light.color.setRGB(LINEAR[i], LINEAR[i + 1], LINEAR[i + 2], THREE.LinearSRGBColorSpace);
}

export class SceneGraph {
  readonly scene = new THREE.Scene();
  /** B8's two lights. Kept in the graph so the scene-graph lint can count them. */
  readonly playerLight: THREE.PointLight;
  readonly bossLight: THREE.PointLight;
  /**
   * §11.16 — the drifting gas field. Owned here rather than by the renderer because it
   * is background in the same sense the parallax lattice is, and because it has to be
   * lit by the same two lights this class already drives: a cloud that disagreed with
   * the floor about where the boss is would be worse than a cloud with no colour at all.
   */
  readonly gas: GasField;

  private readonly floorMesh: THREE.Mesh;
  private readonly floorMaterial: THREE.RawShaderMaterial;
  private readonly parallax: InstancedBatch[] = [];
  /** Layer point sets, in WORLD units on the torus. */
  private readonly fieldX: Float32Array[] = [];
  private readonly fieldY: Float32Array[] = [];
  private readonly fieldJitter: Float32Array[] = [];
  private rippleCursor = 0;

  constructor(arenaWidth: number, arenaHeight: number) {
    this.scene.background = null;

    const floorGeo = buildFloorLattice(FLOOR_CELLS);

    this.floorMaterial = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FLOOR_VERT,
      fragmentShader: FLOOR_FRAG,
      uniforms: {
        // AMD-24: the lattice is resolved to one image on the CPU (it is centred on the
        // camera by construction), so it takes the curve but not the per-vertex wrap.
        ...unwrappedWarpUniforms(),
        uBg: { value: new THREE.Vector3() },
        uGrid: { value: new THREE.Vector3() },
        uGridMajor: { value: new THREE.Vector3() },
        uDecor: { value: new THREE.Vector3() },
        uRipples: { value: RIPPLES },
        uLight0Pos: { value: new THREE.Vector2() },
        uLight0Color: { value: new THREE.Vector3() },
        uLight0Range: { value: 12 },
        uLight1Pos: { value: new THREE.Vector2() },
        uLight1Color: { value: new THREE.Vector3() },
        uLight1Range: { value: 0.001 },
        uTime: { value: 0 },
        uPeriod: { value: new THREE.Vector2(arenaWidth, arenaHeight) },
        uFloorCenter: { value: new THREE.Vector2() },
        uFloorHalf: { value: new THREE.Vector2(60, 60) },
      },
      depthTest: false,
      depthWrite: false,
      transparent: false,
    });
    this.refreshPalette();

    this.floorMesh = new THREE.Mesh(floorGeo, this.floorMaterial);
    this.floorMesh.frustumCulled = false;
    this.floorMesh.matrixAutoUpdate = false;
    this.floorMesh.renderOrder = LAYER_FLOOR;
    this.scene.add(this.floorMesh);

    const quad = buildQuadGeometry();
    for (let i = 0; i < PARALLAX_LAYERS.length; i++) {
      const def = PARALLAX_LAYERS[i];
      const batch = new InstancedBatch(quad, def.count, {
        mode: MODE_SPRITE,
        blend: 'add',
        renderOrder: LAYER_PARALLAX + i,
        name: `parallax-${i}`,
      });
      this.scene.add(batch.mesh);
      this.parallax.push(batch);

      // Fixed point set with a deterministic jitter: a hash of the index, not an RNG
      // stream, so the background costs no draws from `vfx` and never desyncs a capture.
      // AMD-24 promotes these from a [0,1) lattice phase to real world coordinates on the
      // torus, which is what makes the field survive a lap of the arena unchanged.
      const fx = new Float32Array(def.count);
      const fy = new Float32Array(def.count);
      const fj = new Float32Array(def.count);
      for (let k = 0; k < def.count; k++) {
        const h1 = Math.sin((k + 1) * 12.9898 + i * 78.233) * 43758.5453;
        const h2 = Math.sin((k + 1) * 39.3468 + i * 11.135) * 24634.6345;
        const h3 = Math.sin((k + 1) * 4.898 + i * 7.23) * 9871.234;
        fx[k] = (h1 - Math.floor(h1) - 0.5) * WRAP_W;
        fy[k] = (h2 - Math.floor(h2) - 0.5) * WRAP_H;
        fj[k] = h3 - Math.floor(h3);
      }
      this.fieldX.push(fx);
      this.fieldY.push(fy);
      this.fieldJitter.push(fj);
    }

    // Both lights take their colour from the palette (§11.14 criterion 1: no raw hex
    // outside palette.ts). LINEAR already holds linear-space RGB per token.
    this.playerLight = new THREE.PointLight();
    this.playerLight.intensity = 1;
    this.playerLight.distance = 12;
    setLightFromToken(this.playerLight, 'pal.player.aura');
    this.playerLight.castShadow = false;
    this.scene.add(this.playerLight);

    this.bossLight = new THREE.PointLight();
    this.bossLight.intensity = 0;
    this.bossLight.distance = 0.001;
    setLightFromToken(this.bossLight, 'pal.boss.lantern-tyrant.primary');
    this.bossLight.castShadow = false;
    this.scene.add(this.bossLight);

    // Constructed last so its batch is added to the scene after the parallax batches;
    // `renderOrder` decides the draw order regardless, but keeping insertion order and
    // render order agreeing is what makes the scene-graph lint readable.
    this.gas = new GasField(this.scene);
  }

  /** Drives the player aura light. `range` is the Lumensurge-scaled light radius. */
  setPlayerLight(x: number, y: number, colorIndex: number, range: number, intensity: number): void {
    const u = this.floorMaterial.uniforms;
    (u.uLight0Pos.value as THREE.Vector2).set(x, y);
    const ci = colorIndex * 3;
    (u.uLight0Color.value as THREE.Vector3).set(
      LINEAR[ci] * intensity,
      LINEAR[ci + 1] * intensity,
      LINEAR[ci + 2] * intensity,
    );
    u.uLight0Range.value = range;
    this.playerLight.position.set(x, y, 2);
    this.playerLight.distance = range;
    this.playerLight.intensity = intensity;
    this.gas.setLight(0, x, y, colorIndex, range, intensity);
  }

  /** Drives the boss light. `intensity` 0 parks it (still one PointLight, per B8). */
  setBossLight(x: number, y: number, colorIndex: number, range: number, intensity: number): void {
    const u = this.floorMaterial.uniforms;
    (u.uLight1Pos.value as THREE.Vector2).set(x, y);
    const ci = colorIndex * 3;
    (u.uLight1Color.value as THREE.Vector3).set(
      LINEAR[ci] * intensity,
      LINEAR[ci + 1] * intensity,
      LINEAR[ci + 2] * intensity,
    );
    u.uLight1Range.value = Math.max(0.001, range);
    this.bossLight.position.set(x, y, 2);
    this.bossLight.distance = Math.max(0.001, range);
    this.bossLight.intensity = intensity;
    this.gas.setLight(1, x, y, colorIndex, range, intensity);
  }

  /**
   * Per-frame update. Parallax layers past `quality.parallaxLayers` are hidden outright
   * (§14.6 rungs 4–5), which removes their draw call as well as their fill cost.
   */
  update(camX: number, camY: number, viewW: number, viewH: number, time: number): void {
    this.floorMaterial.uniforms.uTime.value = time;
    this.gas.update(camX, camY, viewW, viewH, time);

    // AMD-24: the floor follows the camera instead of covering a fixed arena. The extent
    // is generous because the curvature pulls the lattice INWARD — at the configured
    // maximum the rim of a `max(viewW, viewH)`-sized patch still lands outside the frame
    // corner, so the abyss never shows through at the edge of the screen.
    const u = this.floorMaterial.uniforms;
    (u.uFloorCenter.value as THREE.Vector2).set(camX, camY);
    const half = Math.max(viewW, viewH);
    (u.uFloorHalf.value as THREE.Vector2).set(half, half);

    // Cull margin: a mote is at most `size` across, and pushing one just outside the
    // frame costs nothing while pushing the whole 200 u field would waste 60% of the
    // batch. The cull is also what guarantees the antipodal wrap is never on screen.
    const cullX = viewW * 0.5 + 1;
    const cullY = viewH * 0.5 + 1;
    const layers = quality.parallaxLayers;
    for (let i = 0; i < this.parallax.length; i++) {
      const batch = this.parallax[i];
      if (i >= layers) {
        batch.begin();
        batch.end();
        continue;
      }
      const def = PARALLAX_LAYERS[i];
      const fx = this.fieldX[i];
      const fy = this.fieldY[i];
      const fj = this.fieldJitter[i];
      const depth = def.depth;
      batch.begin();
      for (let k = 0; k < def.count; k++) {
        const dx = wrapDeltaX(fx[k] - camX) * depth;
        if (dx < -cullX || dx > cullX) continue;
        const dy = wrapDeltaY(fy[k] - camY) * depth;
        if (dy < -cullY || dy > cullY) continue;
        const twinkle = 0.6 + 0.4 * Math.sin(time * (0.4 + fj[k] * 0.8) + fj[k] * 6.28);
        const s = def.size * (0.7 + fj[k] * 0.6);
        batch.push(camX + dx, camY + dy, 0, s, s, TOK_DECOR, def.alpha * twinkle, 1.0, 0, 0);
      }
      batch.end();
    }
  }

  /**
   * The scene's half of the boot compile. See `BatchRegistry.setPrecompileVisibility`
   * for why hidden meshes have to be unhidden across `WebGLRenderer.compile`.
   *
   * The floor is excluded: it is a `Mesh` that is visible from the first frame, so it
   * has always been compiled at boot and nothing here needs to change that.
   */
  setPrecompileVisibility(on: boolean): void {
    for (let i = 0; i < this.parallax.length; i++) this.parallax[i].mesh.visible = on;
    this.gas.batch.mesh.visible = on;
  }

  /**
   * AMD-6 D13 — records an explosion ripple. Four slots, oldest recycled, so the sonar
   * grid displaces around a kill for 350 ms and the arena reads as a real space.
   */
  pushRipple(x: number, y: number, time: number): void {
    const r = RIPPLES[this.rippleCursor];
    r.set(x, y, time, 0);
    this.rippleCursor = (this.rippleCursor + 1) % RIPPLES.length;
  }

  /** Boot-time colour refresh after a colourblind preset change. */
  refreshPalette(): void {
    const u = this.floorMaterial.uniforms;
    (u.uBg.value as THREE.Vector3).set(LINEAR[TOK_BG * 3], LINEAR[TOK_BG * 3 + 1], LINEAR[TOK_BG * 3 + 2]);
    (u.uGrid.value as THREE.Vector3).set(LINEAR[TOK_GRID * 3], LINEAR[TOK_GRID * 3 + 1], LINEAR[TOK_GRID * 3 + 2]);
    (u.uGridMajor.value as THREE.Vector3).set(LINEAR[TOK_GRID_MAJOR * 3], LINEAR[TOK_GRID_MAJOR * 3 + 1], LINEAR[TOK_GRID_MAJOR * 3 + 2]);
    (u.uDecor.value as THREE.Vector3).set(LINEAR[TOK_DECOR * 3], LINEAR[TOK_DECOR * 3 + 1], LINEAR[TOK_DECOR * 3 + 2]);
  }

  /** Background clear colour, in linear space, for the scene target. */
  get clearColor(): THREE.Color {
    return new THREE.Color().setRGB(LINEAR[TOK_BG * 3], LINEAR[TOK_BG * 3 + 1], LINEAR[TOK_BG * 3 + 2], THREE.LinearSRGBColorSpace);
  }

  /** Default aura token, so the renderer does not need to know palette names. */
  get auraToken(): number {
    return TOK_PLAYER_AURA;
  }

  dispose(): void {
    this.floorMesh.geometry.dispose();
    this.floorMaterial.dispose();
    for (const p of this.parallax) p.dispose();
    this.gas.dispose();
  }
}
